import React, { useEffect, useState } from 'react';
import { formatTime, formatDateToSlashes } from '../helpers/datesAndTimes';
import JMJButton from './JMJButton';
import { updateAppointment, createAppointment } from '../helpers/appointment';
import { getLocationById } from '../helpers/location';
import StatusTag from './StatusTag';
import { FaLocationDot, FaCalendarDay } from "react-icons/fa6";
import { FaClinicMedical, FaNotesMedical } from "react-icons/fa";
import { getAvailableTimes, getFilteredLocations } from '../helpers/availability';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { getClientById, sendTextMessage } from '../Helpers';
import { getStaffSMSList } from '../helpers/client';

// ---- helpers for booking guards ----
const parseMMDDYYYY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
  if (!mm || !dd || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  return isNaN(d) ? null : d;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

const dedupeById = (rows) => {
  const m = new Map();
  for (const r of rows) {
    if (r?.id && !m.has(r.id)) m.set(r.id, r);
  }
  return Array.from(m.values());
};

const AppointmentCard = ({ isLoading, setIsLoading, client, appointment, onCancel, staffView, checkAppointment }) => {
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('Sick');
  const [customReason, setCustomReason] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [closures, setClosures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [noLanguages, setNoLanguages] = useState([]);

  // Fallback names by numeric id; always coerce lookup with Number()
  const idsToAddresses = {
    0: "JMJ Orlando Clinic",
    1: "JMJ Kissimmee Clinic"
  };

  const [eligibility, setEligibility] = useState({
    unableToAct: false,
    unableToSchedule: false,
    verified: true,
  });

  useEffect(() => {
    if (!client?.id) return;
    const ref = doc(db, 'clients', client.id);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      setEligibility({
        unableToAct: !!data.unableToAct,
        unableToSchedule: !!data.unableToSchedule,
        verified: data.verified !== false,
      });
    }, (err) => console.error('Eligibility snapshot error:', err));
    return () => unsub();
  }, [client?.id]);

  const mustCall = Boolean(
    eligibility.unableToAct || eligibility.unableToSchedule || !eligibility.verified
  );

  const promptCall = () => {
    const msgEn = "Please call to schedule, cancel, or reschedule your appointment.";
    const msgEs = "Por favor llámenos para programar, cancelar o reprogramar su cita.";
    alert(client?.language === "Spanish" ? msgEs : msgEn);
  };

  // Fetch current appointment location; always coerce ID to number and guard against unmount
  useEffect(() => {
    const locIdRaw = appointment?.location;
    if (locIdRaw == null) return;

    let active = true;
    (async () => {
      try {
        const locId = typeof locIdRaw === 'string' ? parseInt(locIdRaw, 10) : Number(locIdRaw);
        const locationData = await getLocationById(locId);
        if (active) setLocation(locationData || null);
      } catch (e) {
        console.error('getLocationById failed', e);
        if (active) setLocation(null);
      }
    })();

    return () => { active = false; };
  }, [appointment?.location]);

  // Initial data load
  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const [appointmentsSnapshot, closuresSnapshot, locationsSnapshot, noLanguagesSnapshot] = await Promise.all([
          getDocs(collection(db, 'appointments')),
          getDocs(collection(db, 'closures')),
          getDocs(collection(db, 'locations')),
          getDocs(collection(db, 'noLanguages')),
        ]);
        setAppointments(appointmentsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        setClosures(closuresSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        setLocations(locationsSnapshot.docs.map(doc => ({ ...doc.data(), id: parseInt(doc.id) })));
        setNoLanguages(noLanguagesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [setIsLoading]);

  // Availability and filtered locations recompute
  useEffect(() => {
    // Ignore fully deleted/cleared appointments (status 5) when computing availability;
    // cancelled (3) still block the slot until staff frees it (availability helper must enforce this too).
    const activeAppointments = appointments.filter(a => a.status !== 5);

    setAvailableTimes(
      getAvailableTimes(
        activeAppointments,
        closures,
        noLanguages,
        selectedDate,
        selectedLocation,
        locations,
        client
      )
    );
  }, [selectedLocation, selectedDate, appointments, closures, noLanguages, locations, client]);

  useEffect(() => {
    setFilteredLocations(getFilteredLocations(selectedDate, locations));
  }, [selectedDate, locations]);

  const sendStaffFacingNotification = async (message) => {
    try {
      // Read which group is active
      const groupDoc = await getDoc(doc(db, 'settings', 'SMS_GROUP'));
      const group = groupDoc.exists() ? (groupDoc.data()?.group || '').toString().toLowerCase() : 'staff';

      let recipients = [];

      if (group === 'staff') {
        const staffUsers = await getStaffSMSList();
        recipients = staffUsers
          .map(s => s.phone?.toString())
          .filter(p => p);
      } else if (group === 'development') {
        const devDoc = await getDoc(doc(db, 'settings', 'SMS_DEVELOPMENT'));
        const numbers = devDoc.exists() ? devDoc.data()?.numbers || [] : [];
        recipients = Array.isArray(numbers) ? numbers.map(n => n.toString()) : [];
      } else {
        console.warn(`Unknown SMS group "${group}", defaulting to no recipients except admin.`);
        recipients = [];
      }

      // Deduplicate
      recipients = Array.from(new Set(recipients));

      const sendPromises = [];

      // Send to group recipients
      recipients.forEach(number => {
        sendPromises.push(
          sendTextMessage({
            message,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: number,
            senderName: "JMJPC"
          })
        );
      });

      // Also send to the same number (trigger email notification) in staff mode
      if (group === 'staff') {
        sendPromises.push(
          sendTextMessage({
            message,
            yourPhoneNumber: "4073059084",
            recipientPhoneNumber: "4073059084",
            senderName: "JMJPC"
          })
        );
      }

      await Promise.all(sendPromises);
    } catch (err) {
      console.error('Error sending staff-facing notification:', err);
    }
  };

  const validateSchedulableDate = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((d - today) / (24 * 60 * 60 * 1000));

    if (diffDays < 0 || diffDays > 5) {
      return client?.language === "Spanish"
        ? "Solo puede programar hasta 5 días en el futuro."
        : "You can only schedule up to 5 days out.";
    }
    if (d.getDay() === 0) {
      return client?.language === "Spanish"
        ? "No hay citas los domingos."
        : "No Sunday appointments are available.";
    }
    return null;
  };

  const handleReschedule = async () => {
    setIsLoading(true);

    const err = validateSchedulableDate(selectedDate);
    if (err) {
      alert(err);
      setIsLoading(false);
      return;
    }

    if (appointment?.id && selectedDate && selectedTime !== '') {
      try {
        let reason = cancelReason;
        if (reason === "Tuve un aborto espontáneo") reason = "Had a miscarriage";
        if (reason === "Tuve un aborto") reason = "Had an abortion";
        if (reason === "Recibí atención prenatal en otro lugar") reason = "Got prenatal care elsewhere";
        const finalReason = (reason === "Other" || reason === "Otro") ? customReason : reason;

        const newLocId = typeof selectedLocation === 'string' ? parseInt(selectedLocation, 10) : Number(selectedLocation);
        const newLocation = await getLocationById(newLocId);

        await updateAppointment(appointment.id, {
          date: formatDateToSlashes(selectedDate),
          location: newLocId,
          startTime: parseInt(selectedTime, 10),
          endTime: parseInt(selectedTime, 10) + 1,
          status: 4,
          rescheduleReason: finalReason
        });

        await sendStaffFacingNotification(
          `JMJPC BOOKING: ${client.firstName} ${client.lastName} (${client.phone}) rescheduled their appointment for ${(parseInt(selectedTime, 10) % 12 === 0 ? 12 : parseInt(selectedTime, 10) % 12)} ${parseInt(selectedTime, 10) >= 12 ? "PM" : "AM"} on ${formatDateToSlashes(selectedDate)} at ${newLocation?.name ?? idsToAddresses[newLocId]}. Reason: "${finalReason}"`
        );

        await sendTextMessage({
          message: client.language === "Spanish"
            ? `JMJPC CITA: Su cita ha sido reprogramada para las ${(parseInt(selectedTime, 10) % 12 === 0 ? 12 : parseInt(selectedTime, 10) % 12)} ${parseInt(selectedTime, 10) >= 12 ? "PM" : "AM"} el ${formatDateToSlashes(selectedDate)} en ${newLocation?.name ?? idsToAddresses[newLocId]}. Por favor traiga una identificación con foto válida.`
            : `JMJPC BOOKING: Your appointment has been rescheduled for ${(parseInt(selectedTime, 10) % 12 === 0 ? 12 : parseInt(selectedTime, 10) % 12)} ${parseInt(selectedTime, 10) >= 12 ? "PM" : "AM"} on ${formatDateToSlashes(selectedDate)} at ${newLocation?.name ?? idsToAddresses[newLocId]}. Please bring a valid photo ID.`,
          yourPhoneNumber: "4073059084",
          recipientPhoneNumber: client.phone,
          senderName: "JMJPC"
        });

        setShowRescheduleModal(false);
        setSelectedTime('');
        checkAppointment();
        setCancelReason('Sick');
        setLocation(newLocation || null);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to reschedule appointment:", error);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    if (appointment?.id) {
      try {
        let reason = cancelReason;
        if (reason === "Tuve un aborto espontáneo") reason = "Had a miscarriage";
        if (reason === "Tuve un aborto") reason = "Had an abortion";
        if (reason === "Recibí atención prenatal en otro lugar") reason = "Got prenatal care elsewhere";
        const finalReason = (reason === "Other" || reason === "Otro") ? customReason : reason;

        await updateAppointment(appointment.id, { status: 3, rescheduleReason: finalReason });
        setShowCancelModal(false);
        onCancel(appointment.id);
        setCancelReason('Sick');

        const apptLocId = Number(appointment.location);
        const apptLocName = idsToAddresses[apptLocId] || location?.name || 'Clinic';
        const apptDate = appointment.date; // Use the appointment’s own date for cancel messages

        await sendStaffFacingNotification(
          `JMJPC BOOKING: ${client.firstName} ${client.lastName} (${client.phone}) cancelled their appointment for ${(parseInt(appointment.startTime, 10) % 12 === 0 ? 12 : parseInt(appointment.startTime, 10) % 12)} ${parseInt(appointment.startTime, 10) >= 12 ? "PM" : "AM"} on ${apptDate} at ${apptLocName}. Reason: "${finalReason}"`
        );

        await sendTextMessage({
          message: client.language === "Spanish"
            ? `JMJPC CITA: Su cita para las ${(parseInt(appointment.startTime, 10) % 12 === 0 ? 12 : parseInt(appointment.startTime, 10) % 12)} ${parseInt(appointment.startTime, 10) >= 12 ? "PM" : "AM"} el ${apptDate} en ${apptLocName} ha sido cancelada.`
            : `JMJPC BOOKING: Your appointment for ${(parseInt(appointment.startTime, 10) % 12 === 0 ? 12 : parseInt(appointment.startTime, 10) % 12)} ${parseInt(appointment.startTime, 10) >= 12 ? "PM" : "AM"} on ${apptDate} at ${apptLocName} has been cancelled.`,
          yourPhoneNumber: "4073059084",
          recipientPhoneNumber: client.phone,
          senderName: "JMJPC"
        });

        setIsLoading(false);
      } catch (error) {
        console.error("Failed to cancel appointment:", error);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleBooking = async () => {
    const err = validateSchedulableDate(selectedDate);
    if (err) {
      alert(err);
      return;
    }

    if (!selectedDate || selectedTime === '' || !selectedLocation || !client) {
      alert('Please fill out all fields');
      return;
    }

    // Guard against blocked / ineligible client before booking
    if (eligibility.unableToAct || eligibility.unableToSchedule || !eligibility.verified) {
      const msgEn = 'Online booking is disabled for your account. Please call the center.';
      const msgEs = 'La programación en línea está deshabilitada para su cuenta. Por favor llame al centro.';
      alert(client?.language === 'Spanish' ? msgEs : msgEn);
      return;
    }

    setIsLoading(true);
    try {
      const locId = typeof selectedLocation === 'string' ? parseInt(selectedLocation, 10) : Number(selectedLocation);
      const slotDate = formatDateToSlashes(selectedDate);
      const slotHour = parseInt(selectedTime, 10);

      const clientPhone = client?.phone?.toString();
      const clientId = client?.id?.toString();
      const clientKeys = uniq([clientPhone, clientId]);

      if (!clientPhone) {
        alert(
          client?.language === 'Spanish'
            ? 'No pudimos verificar su número de teléfono. Por favor llame al centro.'
            : 'We could not verify your phone number. Please call the center.'
        );
        setIsLoading(false);
        return;
      }

      const appointmentsRef = collection(db, 'appointments');
      const today0 = startOfToday();

      // 1) Block if client already has an upcoming Scheduled (0) or Rescheduled (4) appointment.
      // We check both legacy keys (phone and id) to avoid mismatches.
      const activeSnaps = await Promise.all(
        clientKeys.map((key) =>
          getDocs(
            query(
              appointmentsRef,
              where('client', '==', key),
              where('status', 'in', [0, 4])
            )
          )
        )
      );

      const activeRows = dedupeById(
        activeSnaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      );

      const hasUpcomingActive = activeRows.some((app) => {
        const d = parseMMDDYYYY(app.date);
        if (!d) return false;
        d.setHours(0, 0, 0, 0);
        return d >= today0;
      });

      if (hasUpcomingActive) {
        alert(
          client.language === 'Spanish'
            ? 'Ya tiene una cita programada. Por favor cancele o reprograme antes de programar otra.'
            : 'You already have an appointment scheduled. Please cancel or reschedule it before booking another.'
        );
        setIsLoading(false);
        return;
      }

      // 2) Booking-time slot check (defensive): cancelled-but-not-cleared must block.
      // Only status 5 should free a slot; we treat 0-4 as occupying.
      const slotSnap = await getDocs(
        query(
          appointmentsRef,
          where('date', '==', slotDate),
          where('startTime', '==', slotHour),
          where('location', '==', locId),
          where('status', 'in', [0, 1, 2, 3, 4])
        )
      );

      if (!slotSnap.empty) {
        alert(
          client.language === 'Spanish'
            ? 'Ese horario ya no está disponible. Por favor elija otro.'
            : 'That time slot is no longer available. Please pick another.'
        );
        setIsLoading(false);
        return;
      }

      // 3) LAST GUARDRAIL: check by phone number only (and both Firestore types: string + number)
      // This catches weird cases where earlier checks miss because existing docs stored `client` as a number.
      const phoneAsNumber = Number(clientPhone);
      const phoneKeys = uniq([
        clientPhone,
        Number.isNaN(phoneAsNumber) ? null : phoneAsNumber
      ]);

      const phoneSnaps = await Promise.all(
        phoneKeys.map((k) =>
          getDocs(
            query(
              appointmentsRef,
              where('client', '==', k),
              where('status', 'in', [0, 4])
            )
          )
        )
      );

      const phoneRows = dedupeById(
        phoneSnaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      );

      const hasUpcomingByPhone = phoneRows.some((app) => {
        const d = parseMMDDYYYY(app.date);
        if (!d) return false;
        d.setHours(0, 0, 0, 0);
        return d >= today0;
      });

      if (hasUpcomingByPhone) {
        alert(
          client.language === 'Spanish'
            ? 'Ya tiene una cita programada. Por favor cancele o reprograme antes de programar otra.'
            : 'You already have an appointment scheduled. Please cancel or reschedule it before booking another.'
        );
        setIsLoading(false);
        return;
      }

      // 4) Create appointment — canonicalize `client` to phone going forward
      await createAppointment({
        date: slotDate,
        startTime: slotHour,
        endTime: slotHour + 1,
        location: locId,
        client: clientPhone,      // IMPORTANT: canonical key for "who owns this appt"
        clientId: clientId || '', // optional: helps with legacy/debugging
        status: 0,
        rescheduleReason: '',
      });

      const newLocation = await getLocationById(locId);
      setLocation(newLocation || null);

      await sendStaffFacingNotification(
        `JMJPC BOOKING: ${client.firstName} ${client.lastName} (${client.phone}) booked an appointment for ${(slotHour % 12 === 0 ? 12 : slotHour % 12)} ${slotHour >= 12 ? "PM" : "AM"} on ${slotDate} at ${newLocation?.name ?? idsToAddresses[locId]}.`
      );

      await sendTextMessage({
        message: client.language === "Spanish"
          ? `JMJPC CITA: Ha programado exitosamente una cita para las ${(slotHour % 12 === 0 ? 12 : slotHour % 12)} ${slotHour >= 12 ? "PM" : "AM"} el ${slotDate} en ${newLocation?.name ?? idsToAddresses[locId]}. Por favor traiga una identificación con foto válida.`
          : `JMJPC BOOKING: You have successfully booked an appointment for ${(slotHour % 12 === 0 ? 12 : slotHour % 12)} ${slotHour >= 12 ? "PM" : "AM"} on ${slotDate} at ${newLocation?.name ?? idsToAddresses[locId]}. Please bring a valid photo ID.`,
        yourPhoneNumber: "4073059084",
        recipientPhoneNumber: client.phone,
        senderName: "JMJPC"
      });

      checkAppointment(); // Refresh state in parent
      setIsLoading(false);
    } catch (error) {
      console.error('Error booking appointment:', error);
      setIsLoading(false);
      alert(
        client?.language === 'Spanish'
          ? 'Ocurrió un error al programar la cita. Por favor intente de nuevo.'
          : 'There was an error booking the appointment. Please try again.'
      );
    }
  };

  // UI helpers for location display fallbacks
  const currentLocationName =
    location?.name ??
    (appointment?.location != null ? idsToAddresses[Number(appointment.location)] : undefined) ??
    "Loading...";

  const currentLocationAddress =
    location?.address ?? "Loading...";

  return (
    <div className="relative text-black bg-JMJGray p-4 rounded-xl text-left w-[95%] md:w-[35%] h-[500px] flex overflow-hidden self-center">
      <div className={`flex-1 ${isLoading ? 'opacity-50' : ''} overflow-auto`}>
        {appointment ? (
          <>
            <StatusTag status={appointment.status} language={!staffView && client.language === "Spanish" ? "Spanish" : "English"} />
            <p className="text-2xl text-white font-bold">
              {!staffView && client.language === "Spanish" ? "Cita pendiente" : "Appointment"}: {client?.firstName} {client?.lastName}
            </p>

            <div className="flex items-center gap-2">
              <FaClinicMedical className="text-white" />
              <p className="text-white font-bold">{currentLocationName}</p>
            </div>

            <div className="flex items-center gap-2">
              <FaLocationDot className="text-white" />
              <p className="text-white font-bold">{currentLocationAddress}</p>
            </div>

            <div className='flex items-center gap-2'>
              <FaCalendarDay className='text-white' />
              <p className="text-white font-bold">{appointment.date} • {formatTime(appointment.startTime)}</p>
            </div>

            {!!client?.notes && String(client.notes).trim() !== "" && (
              <div className='flex items-center gap-2 text-white'>
                <FaNotesMedical />
                <p className='italic'>"{client.notes}"</p>
              </div>
            )}

            <JMJButton
              onClick={mustCall ? promptCall : () => setShowCancelModal(true)}
              color="green"
              text={!staffView && client.language === "Spanish" ? "Cancelar" : "Cancel"}
              className="mt-1"
            />

            <JMJButton
              onClick={mustCall ? promptCall : () => setShowRescheduleModal(true)}
              color="green"
              text={!staffView && client.language === "Spanish" ? "Reprogramar" : "Reschedule"}
              className="mt-1 ml-2"
            />

            {showCancelModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] md:w-[400px]">
                  <h2 className="text-xl font-bold mb-4">
                    {client.language === "Spanish" ? "Seleccione un motivo de cancelación." : "Select a Cancellation Reason"}
                  </h2>
                  <select className="w-full p-2 border rounded-lg mb-3" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
                    {(client.language === "Spanish"
                      ? ["Enfermo", "Tuve un aborto espontáneo", "Tuve un aborto", "Recibí atención prenatal en otro lugar", "Otro"]
                      : ["Sick", "Had a miscarriage", "Had an abortion", "Got prenatal care elsewhere", "Other"]
                    ).map((reason, idx) => (
                      <option key={idx} value={reason}>{reason}</option>
                    ))}
                  </select>
                  {(cancelReason === "Other" || cancelReason === "Otro") && (
                    <input
                      type="text"
                      maxLength={500}
                      placeholder={client.language === "Spanish" ? "Ingrese un motivo personalizado" : "Enter custom reason"}
                      className="w-full p-2 border rounded-lg mb-3"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  )}
                  <div className="flex justify-end gap-2">
                    <JMJButton text={client.language === "Spanish" ? "Cancelar" : "Cancel"} color="gray" onClick={() => setShowCancelModal(false)} />
                    {((cancelReason !== "Other" && cancelReason !== "Otro") || customReason !== "") && (
                      <JMJButton text={client.language === "Spanish" ? "Confirmar" : "Confirm"} color="green" onClick={handleCancel} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {showRescheduleModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] md:w-[400px]">
                  <h2 className="text-xl font-bold mb-4">{client.language === "Spanish" ? "Reprogramar cita" : "Reschedule Appointment"}</h2>

                  <label className="mt-2">{client.language === "Spanish" ? "Fecha" : "Date"}:
                    <input
                      className="text-black w-full p-2 border rounded-lg mb-3"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        // requested behavior: changing date resets dependent fields
                        setSelectedDate(e.target.value);
                        setSelectedLocation('');
                        setSelectedTime('');
                        setAvailableTimes([]);
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      max={new Date(new Date().setDate(new Date().getDate() + 5)).toISOString().split('T')[0]}
                    />
                  </label>

                  <label className="mt-2">{client.language === "Spanish" ? "Ubicación" : "Location"}:
                    <select
                      value={selectedLocation}
                      onChange={(e) => {
                        setSelectedLocation(e.target.value);
                        setSelectedTime(''); // optional: changing location also clears time
                      }}
                      className="w-full p-2 border rounded-lg mb-3"
                    >
                      <option value="">{client.language === "Spanish" ? "Seleccione ubicación" : "Select Location"}</option>
                      {filteredLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                    </select>
                  </label>

                  <label className="mt-2">{client.language === "Spanish" ? "Hora" : "Time"}:
                    <select
                      value={selectedTime}
                      className="w-full p-2 border rounded-lg mb-3"
                      onChange={(e) => setSelectedTime(e.target.value)}
                    >
                      <option value="">{client.language === "Spanish" ? "Seleccione hora" : "Select Time"}</option>
                      {availableTimes.map(hour => (
                        <option key={hour} value={hour}>{hour % 12 === 0 ? 12 : hour % 12}:00 {hour < 12 ? 'AM' : 'PM'}</option>
                      ))}
                    </select>
                  </label>

                  <label className="mt-2">{client.language === "Spanish" ? "Razón" : "Reason"}:
                    <select
                      className="w-full p-2 border rounded-lg mb-3"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                    >
                      {(client.language === "Spanish"
                        ? ["Enfermo", "Conflicto de horario", "Error al programar anteriormente", "Otro"]
                        : ["Sick", "Scheduling Conflict", "Mistake Scheduling Previously", "Other"]
                      ).map((reason, idx) => (
                        <option key={idx} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </label>

                  {(cancelReason === "Other" || cancelReason === "Otro") && (
                    <input
                      type="text"
                      maxLength={500}
                      placeholder={client.language === "Spanish" ? "Ingrese un motivo personalizado" : "Enter custom reason"}
                      className="w-full p-2 border rounded-lg mb-3"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  )}

                  <div className="flex justify-end gap-2">
                    <JMJButton text={client.language === "Spanish" ? "Cancelar" : "Cancel"} color="gray" onClick={() => setShowRescheduleModal(false)} />
                    {((cancelReason !== "Other" && cancelReason !== "Otro") || customReason !== "") && selectedDate && selectedTime && selectedLocation && (
                      <JMJButton text={client.language === "Spanish" ? "Confirmar" : "Confirm"} color="green" onClick={handleReschedule} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-start text-white">
            <h2 className="text-2xl font-bold mb-2">{client?.language === 'English' ? 'Book an Appointment' : 'Reservar una Cita'}</h2>
            <i className="pb-2">{client?.language === 'English' ? 'Appointments are 1 hour in length and can only be booked up to 5 days in advance.' : 'Las citas tienen una duración de 1 hora y solo se pueden reservar con hasta 5 días de anticipación.'}</i>

            <label className="mt-2 text-white">{client?.language === 'English' ? "Date:" : "Fecha:"}
              <input
                className="text-black w-full p-2 border rounded-lg"
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  // Optional symmetry: date change resets dependent fields in booking flow too
                  setSelectedLocation('');
                  setSelectedTime('');
                  setAvailableTimes([]);
                }}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(new Date().setDate(new Date().getDate() + 5)).toISOString().split('T')[0]}
              />
            </label>

            <label className="mt-2 text-white">{client?.language === 'English' ? "Location:" : "Ubicación:"}
              <select
                value={selectedLocation}
                className='text-black w-full p-2 border rounded-lg'
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setSelectedTime(''); // optional: changing location clears time
                }}
              >
                <option value="">{client?.language === 'English' ? "Select Location:" : "Seleccione ubicación:"}</option>
                {filteredLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select>
            </label>

            {selectedLocation && selectedDate && (
              <label className="mt-2 text-white">{client?.language === 'English' ? "Time:" : "Hora:"}
                <select
                  value={selectedTime}
                  className='text-black w-full p-2 border rounded-lg'
                  onChange={(e) => setSelectedTime(e.target.value)}
                >
                  <option value="">{client?.language === 'English' ? "Select Time:" : "Seleccione hora:"}</option>
                  {availableTimes.map(hour => (
                    <option key={hour} value={hour}>{hour % 12 === 0 ? 12 : hour % 12}:00 {hour < 12 ? 'AM' : 'PM'}</option>
                  ))}
                </select>
              </label>
            )}

            <JMJButton
              color="green"
              onClick={mustCall ? promptCall : handleBooking}
              text={client?.language === 'English' ? 'Book Appointment' : 'Reservar Cita'}
              className="mt-4"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCard;
